import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../../api/axiosInstance";
import { Copy, WhatsApp } from "../../shared/icon";
import Dialog from "../../shared/Dialog";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { getBalanceCRefAsync } from "../../store/slices/betSlice";
import { fetchWalletSummary } from "../../store/slices/walletSlice";
import "./deposite.css";
import { Link, useNavigate } from "react-router-dom";

const DEFAULT_DEPOSIT_MIN = 100;
const DEFAULT_DEPOSIT_MAX = 100000;

function extractLimitsPayload(apiData) {
  if (!apiData || typeof apiData !== "object") return null;
  if ("deposit_min_limit" in apiData || "deposit_max_limit" in apiData) {
    return apiData;
  }
  const inner = apiData.data;
  if (
    inner &&
    typeof inner === "object" &&
    ("deposit_min_limit" in inner || "deposit_max_limit" in inner)
  ) {
    return inner;
  }
  return null;
}

function resolveDepositLimits(raw) {
  const minRaw = raw?.deposit_min_limit;
  const maxRaw = raw?.deposit_max_limit;
  let min = Number(minRaw);
  let max = Number(maxRaw);
  const minOk = Number.isFinite(min) && min > 0;
  const maxOk = Number.isFinite(max) && max > 0;
  if (!minOk) min = DEFAULT_DEPOSIT_MIN;
  if (!maxOk) max = DEFAULT_DEPOSIT_MAX;
  if (min > max) {
    min = DEFAULT_DEPOSIT_MIN;
    max = DEFAULT_DEPOSIT_MAX;
  }
  return { depositMin: min, depositMax: max };
}

function formatInrAmount(n) {
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString("en-IN");
}

function getAccountLimitMax(acc) {
  const max = Number(acc?.max_amount);
  const total = Number(acc?.total_max_amount);
  if (Number.isFinite(total) && total > 0) return total;
  if (Number.isFinite(max) && max > 0) return max;
  return null;
}

function amountFitsAccount(amt, acc) {
  if (!acc) return true;
  const min = Number(acc.min_amount);
  const maxCap = getAccountLimitMax(acc);
  if (Number.isFinite(min) && min > 0 && amt < min) {
    return { ok: false, reason: "min", min, max: maxCap };
  }
  if (maxCap != null && amt > maxCap) {
    return {
      ok: false,
      reason: "max",
      min: Number.isFinite(min) ? min : null,
      max: maxCap,
    };
  }
  return { ok: true };
}

const getBackendErrorMessage = (err) => {
  if (!err) return "Something went wrong";
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    return (
      err?.msg ||
      err?.message ||
      err?.error ||
      err?.data?.msg ||
      err?.data?.message ||
      err?.response?.data?.msg ||
      err?.response?.data?.message ||
      "Something went wrong"
    );
  }
  return "Something went wrong";
};

function normalizeApiList(data) {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") {
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.list)) return data.list;
    if (Array.isArray(data.records)) return data.records;
  }
  return [];
}

function isRowActive(row) {
  if (row?.status === undefined || row?.status === null) return true;
  return row.status === true || row.status === "true" || row.status === 1;
}

function getEntityId(entity) {
  if (!entity || typeof entity !== "object") return "";
  return entity._id ?? entity.id ?? entity.bank_account_type_id ?? "";
}

function getPaymentTypeImage(pt) {
  if (!pt) return null;
  const meta = pt.content_meta;
  return (
    pt.image ||
    (meta &&
      typeof meta === "object" &&
      (meta.image || meta.url || meta.icon)) ||
    (typeof meta === "string" ? meta : null)
  );
}

function getPaymentTypeLabel(pt) {
  return pt?.name || pt?.title || pt?.display_name || pt?.label || "—";
}

const Deposite = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
 const whatsappUrl = useAppSelector((state) => state.sidebar?.whatsappUrl || "");
  console.log("whatsappUrl: ", whatsappUrl);
  const handleCopy = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => {
      setCopiedField(null);
    }, 2000);
  };

  const { transactions, loading: loadingWallet } = useAppSelector(
    (state) => state.wallet || {},
  );
  const depositTransactions = useMemo(() => {
    return (transactions || []).filter((t) => {
      return t.transactionType === "DEPOSIT";
    });
  }, [transactions]);

  useEffect(() => {
    dispatch(getBalanceCRefAsync());

    const toDateObj = new Date();
    const fromDateObj = new Date();
    fromDateObj.setDate(fromDateObj.getDate() - 30);
    dispatch(
      fetchWalletSummary({
        from_date: fromDateObj.toISOString(),
        to_date: toDateObj.toISOString(),
      }),
    );
  }, [dispatch]);

  const [amount, setAmount] = useState("");
  const [copiedField, setCopiedField] = useState(null);
  const [activeTabId, setActiveTabId] = useState("");
  const [step, setStep] = useState(0);

  const [walletLimits, setWalletLimits] = useState(null);
  const [loadingLimits, setLoadingLimits] = useState(true);
  const [isProcessingOCR, setIsProcessingOCR] = useState(false);
  const [refNoSource, setRefNoSource] = useState(null);

  const { depositMin, depositMax } = useMemo(() => {
    if (!walletLimits) {
      return {
        depositMin: DEFAULT_DEPOSIT_MIN,
        depositMax: DEFAULT_DEPOSIT_MAX,
      };
    }
    return resolveDepositLimits(walletLimits);
  }, [walletLimits]);

  useEffect(() => {
    const fetchWalletLimits = async () => {
      setLoadingLimits(true);
      try {
        const res = await axiosInstance.post("/user/getUserShoutPeWalletLimit");
        if (res?.status) {
          const limits =
            extractLimitsPayload(res?.data) ?? extractLimitsPayload(res);
          if (limits) setWalletLimits(limits);
        }
      } catch (err) {
        toast.error(getBackendErrorMessage(err));
      } finally {
        setLoadingLimits(false);
      }
    };
    fetchWalletLimits();
  }, []);

  const [depositNotifyDescription, setDepositNotifyDescription] =
    useState("");


  useLayoutEffect(() => {
    const fetchDepositAnnouncement = async () => {
      try {
        const res = await axiosInstance.post("/announcement/getNotifyInfo", {
          page: "deposit",
        });
        const text = res?.data?.description;
        if (res?.status && typeof text === "string" && text.trim()) {
          setDepositNotifyDescription(text.trim());
        }
      } catch {
        // optional notice; do not block deposit flow
      }
    };
    fetchDepositAnnouncement();
  }, []);

  const [paymentTypes, setPaymentTypes] = useState([]);
  const [loadingPaymentTypes, setLoadingPaymentTypes] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);

  const [bankAccounts, setBankAccounts] = useState([]);
  const [loadingBankAccounts, setLoadingBankAccounts] = useState(false);

  const [utrNumber, setUtrNumber] = useState("");
  const [proofFile, setProofFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showTermsModal, setShowTermsModal] = useState(false);

  const compressImage = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = String(event.target?.result ?? "");
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          const MAX_WIDTH = 1920;
          const MAX_HEIGHT = 1920;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          if (!ctx) {
            resolve(file);
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);

          let quality = 0.9;
          const tryCompress = () => {
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  resolve(file);
                  return;
                }
                if (blob.size > 1024 * 1024 && quality > 0.1) {
                  quality -= 0.1;
                  tryCompress();
                  return;
                }
                resolve(
                  new File([blob], file.name, {
                    type: "image/jpeg",
                    lastModified: Date.now(),
                  }),
                );
              },
              "image/jpeg",
              quality,
            );
          };
          tryCompress();
        };
        img.onerror = () => resolve(file);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const extractTransactionId = (text) => {
    const cleanText = (text || "").replace(/\s+/g, "");

    const utrPattern = /UTR[:\s]*(\d{12,16})/gi;
    const utrMatch = (text || "").match(utrPattern);
    if (utrMatch && utrMatch.length > 0) {
      const utrNumber = utrMatch[0].replace(/\D/g, "");
      if (utrNumber.length >= 12) return utrNumber;
    }

    const upiPatterns = [
      /UPI[:\s]*(?:Transaction|Ref|ID)[:\s]*(\d{12,26})/gi,
      /(?:UPI|Reference|Ref)[:\s]*(?:No|Number|ID)[:\s]*[:]?\s*(\d{12,26})/gi,
      /Transaction[:\s]*(?:ID|Reference)[:\s]*(\d{12,26})/gi,
    ];

    for (const pattern of upiPatterns) {
      const matches = (text || "").match(pattern);
      if (matches && matches.length > 0) {
        const numbers = matches[0].replace(/\D/g, "");
        if (numbers.length >= 12) return numbers;
      }
    }

    const twelveDigitPattern = /\b\d{12}\b/g;
    const twelveDigitMatches = cleanText.match(twelveDigitPattern);
    if (twelveDigitMatches && twelveDigitMatches.length > 0) {
      return twelveDigitMatches[0];
    }

    const bankRefPattern = /\b\d{12,16}\b/g;
    const bankRefMatches = cleanText.match(bankRefPattern);
    if (bankRefMatches && bankRefMatches.length > 0) {
      return bankRefMatches[0];
    }

    const longPattern = /\b\d{16,26}\b/g;
    const longMatches = cleanText.match(longPattern);
    if (longMatches && longMatches.length > 0) {
      return longMatches[0];
    }

    const allDigits = cleanText.match(/\d+/g);
    if (allDigits) {
      const longNumbers = allDigits.filter((num) => num.length >= 12);
      if (longNumbers.length > 0) {
        longNumbers.sort((a, b) => a.length - b.length);
        return longNumbers[0];
      }
    }

    return null;
  };

  const looksLikeFailedPaymentSlip = (text) => {
    const t = (text || "").toLowerCase();
    return (
      t.includes("failed") ||
      t.includes("declined") ||
      t.includes("unsuccessful") ||
      t.includes("reversed") ||
      t.includes("refund") ||
      t.includes("refunded")
    );
  };

  const tryAutoExtractUtrFromImage = async (file) => {
    try {
      const imageUrl = URL.createObjectURL(file);
      const Tesseract = await import("tesseract.js");
      const {
        data: { text },
      } = await Tesseract.recognize(imageUrl, "eng");
      URL.revokeObjectURL(imageUrl);
      return {
        extracted: extractTransactionId(text || ""),
        isFailedSlip: looksLikeFailedPaymentSlip(text || ""),
      };
    } catch {
      return { extracted: null, isFailedSlip: false };
    }
  };

  const handleCapture = (e) => {
    (async () => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File upload error: The file size must be less than 5MB");
        e.target.value = "";
        setProofFile(null);
        return;
      }

      setIsProcessingOCR(true);

      if (refNoSource === "auto") {
        setUtrNumber("");
        setRefNoSource(null);
      }

      let fileToUpload = file;
      try {
        const compressed = await compressImage(file);
        fileToUpload = compressed;
      } catch {
        // use original file
      }

      setProofFile(fileToUpload);

      const { extracted, isFailedSlip } =
        await tryAutoExtractUtrFromImage(fileToUpload);

      if (isFailedSlip) {
        if (refNoSource !== "manual") {
          setUtrNumber("");
          setRefNoSource(null);
        }
        toast.error(
          "This screenshot looks like a failed/declined payment. Please upload a successful payment slip.",
        );
      } else if (extracted) {
        setUtrNumber(extracted);
        setRefNoSource("auto");
        setFormErrors((prev) => ({ ...prev, utrNumber: null }));
        toast.success(`Reference ID detected: ${extracted}`);
      } else {
        if (refNoSource !== "manual") {
          setUtrNumber("");
          setRefNoSource(null);
        }
        toast.error(
          "Could not automatically detect UTR from image. Please enter it manually.",
        );
      }

      setIsProcessingOCR(false);
    })();
  };

  const fetchPaymentTypes = async (e) => {
    e.preventDefault();
    const amt = parseInt(amount, 10);
    if (!amt || amt <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (amt < depositMin || amt > depositMax) {
      toast.error(`Amount must be between ${depositMin} and ${depositMax}`);
      return;
    }
    setLoadingPaymentTypes(true);
    try {
      const res = await axiosInstance.post("/wallet/bankAccountTypesGet", {
        payment_type: "DEPOSIT",
        amount: amt,
      });
      const methods = normalizeApiList(res?.data || res).filter(isRowActive);
      if (methods.length > 0) {
        setPaymentTypes(methods);
        setStep(1);
        const firstId = getEntityId(methods[0]);
        setActiveTabId(firstId);
        fetchBankAccounts(methods[0]);
      } else {
        toast.error(res?.msg || "No payment methods available");
      }
    } catch (err) {
      toast.error(getBackendErrorMessage(err));
    } finally {
      setLoadingPaymentTypes(false);
    }
  };

  const fetchBankAccounts = async (typeItem) => {
    const amt = parseInt(amount, 10);
    const typeId = getEntityId(typeItem);
    if (!typeId) return;

    setSelectedPaymentType(typeItem);
    setLoadingBankAccounts(true);
    setBankAccounts([]);
    try {
      const res = await axiosInstance.post("/wallet/getParentBankAccount", {
        bank_account_type_id: typeId,
        amount: amt,
      });
      const accounts = normalizeApiList(res?.data || res).filter(isRowActive);
      if (accounts.length > 0) {
        setBankAccounts(accounts);
      } else {
        toast.error(res?.msg || "No bank accounts available");
      }
    } catch (err) {
      toast.error(getBackendErrorMessage(err));
    } finally {
      setLoadingBankAccounts(false);
    }
  };

  const handleDepositSubmit = async (accId) => {
    const acc = bankAccounts.find((a) => getEntityId(a) === accId);
    const amt = parseInt(amount, 10);
    const fit = amountFitsAccount(amt, acc);

    if (!fit.ok) {
      if (fit.reason === "max") {
        toast.error(
          `For this account, amount must be at most ₹${formatInrAmount(fit.max)}`,
        );
      } else {
        toast.error(
          `For this account, amount must be at least ₹${formatInrAmount(fit.min)}`,
        );
      }
      return;
    }

    const errors = {};
    if (!utrNumber.trim()) {
      errors.utrNumber = "Please enter your UTR ID";
    }
    if (!proofFile) {
      errors.proofFile = "Please upload payment proof";
    }
    if (!termsAccepted) {
      errors.termsAccepted = "Please check terms and condition";
    }

    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("image", proofFile);
      formData.append("bank_account_id", accId);
      formData.append("amount", amount);
      formData.append("remark", "deposit");
      formData.append("user_reference_no", utrNumber);

      const res = await axiosInstance.post(
        "/wallet/depositRequestInit",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (res?.status) {
        toast.success(res?.msg || "Deposit request submitted successfully!");
        setStep(0);
        setAmount("");
        setUtrNumber("");
        setProofFile(null);
        setFormErrors({});
        dispatch(getBalanceCRefAsync());
      } else {
        toast.error(res?.msg || "Failed to submit deposit request");
      }
    } catch (err) {
      toast.error(getBackendErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const depositTabs = useMemo(() => {
    return paymentTypes.map((pt) => ({
      id: getEntityId(pt),
      label: getPaymentTypeLabel(pt),
      icon: getPaymentTypeImage(pt),
      pt: pt,
    }));
  }, [paymentTypes]);

  const amountShortcuts = [500, 1000, 5000, 10000, 50000, 100000];

  const handleAmountShortcutClick = (incrementBy) => {
    setAmount((prev) => {
      const currentAmount = parseInt(prev, 10) || 0;
      return String(currentAmount + incrementBy);
    });
  };

  const isPaytmSelected = getPaymentTypeLabel(selectedPaymentType)
    .toLowerCase()
    .includes("paytm");

  return (
    <div className="deposite-container">
      <h2 className="userscreen-title">
        <button className="btn-xs casino-back" onClick={() => navigate(-1)}>
          Back
        </button>
      </h2>

      <div className="bankingUi">
        <div className="container-fluid h-100">
          <div className="row h-100">
            <div
              className="col-md-12 text-center pt-3 logo-section"
              style={{ display: "none" }}
            >
              <img
                className="img-fluid main-logo"
                src="https://speedcdn.io/assets/logos/reddybook.club.png"
                style={{ width: "150px" }}
                alt="Reddybook Logo"
              />
              <h2>Rider12r(0)</h2>
            </div>
            <div className="col-md-8">
              <div className="row">
                <div className="col-md-12">
                  <div className="row m-0">
                  <span className="mt-2" style={{ fontSize: "14px" }}>
                    Disclaimer: For faster and more reliable payments, please use the bank transfer option.
                  </span>
                  {depositNotifyDescription ? (
                    <div
                      className="deposite-announcement-banner mt-1"
                      role="status"
                      aria-live="polite"
                    >
                      <span
                        className="deposite-announcement-icon"
                        aria-hidden="true"
                      >
                        📢
                      </span>
                      <div className="deposite-announcement-text">
                        {depositNotifyDescription}
                      </div>
                    </div>
                  ) : null}
                    {step === 0 && (
                      <div
                        className="col-md-12 card amount-div"
                        style={{ marginTop: "10px" }}
                      >
                        <div className="card-body col-md-12 !p-5">
                          <form id="amountForm" onSubmit={fetchPaymentTypes}>
                            <input
                              type="hidden"
                              name="_token"
                              value="VVipBm4yQFdesyc9usQU1nfmiXAAPD5G5RxkADsL"
                            />{" "}
                            <div className="form-group mb-0">
                              <label>Amount</label>
                              <div className="input-group">
                                <input
                                  type="number"
                                  className="form-control"
                                  name="amount"
                                  id="amount"
                                  placeholder="Enter amount"
                                  value={amount}
                                  onChange={(e) => setAmount(e.target.value)}
                                />
                                <div className="input-group-append">
                                  <button
                                    type="submit"
                                    className="input-group-text amountBtn"
                                    disabled={
                                      loadingPaymentTypes || loadingLimits
                                    }
                                  >
                                    {loadingPaymentTypes
                                      ? "LOADING..."
                                      : "SUBMIT"}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </form>
                        </div>
                      </div>
                    )}

                    {step === 1 && (
                      <>
                        <div className="col-md-12 account-list">
                          <nav>
                            <button
                              className="btn btn-primary back_to_amount"
                              onClick={() => setStep(0)}
                            >
                              &lt; Back
                            </button>
                            <div
                              className="nav nav-tabs"
                              id="nav-tab"
                              role="tablist"
                              style={{
                                overflow: "hidden",
                                overflowX: "scroll",
                              }}
                            >
                            {
                              whatsappUrl && (
                                <button
                                  key="whatsapp"
                                  type="button"
                                  className="nav-link d-flex flex-column align-items-center justify-content-center"
                                  onClick={() => window.open(whatsappUrl, "_blank")}
                                ><span>Whatsapp </span>
                                   <i
                className="bi bi-whatsapp"
                style={{ fontSize: 24, color: "#25D366" }}
              /> 
                                </button>
                              )
                            }
                              {depositTabs.map((tab) => (
                                <button
                                  key={tab.id}
                                  type="button"
                                  className={`nav-link ${activeTabId === tab.id ? "active" : ""}`}
                                  id={`${tab.id}-tab`}
                                  role="tab"
                                  onClick={() => {
                                    if (tab.onClick) {
                                      tab.onClick();
                                    } else {
                                      setActiveTabId(tab.id);
                                      if (tab.pt) {
                                        fetchBankAccounts(tab.pt);
                                      }
                                    }
                                  }}
                                >
                                  {tab.label}{" "}
                                  <img
                                    src={tab.icon}
                                    width="5%"
                                    alt={tab.label}
                                  />
                                </button>
                              ))}
                            </div>
                          </nav>
                          <div className="tab-content" id="nav-tabContent">
                            {loadingBankAccounts ? (
                              <div className="text-center p-5">
                                Loading bank accounts...
                              </div>
                            ) : (
                              bankAccounts.map((acc) => (
                                <div
                                  key={acc._id || acc.id}
                                  className={`tab-pane fade show active`}
                                  role="tabpanel"
                                >
                                  <div className="row">
                                    <div className="col-md-12">
                                      <div className="card">
                                        <div className="card-body p-3">
                                          <h5 className="card-title">
                                            {selectedPaymentType?.name ||
                                              "Bank Account"}
                                          </h5>
                                          <div className="row">
                                            <div className="col-md-6">
                                              <div className="row justify-content-center">
                                                <div className="col-md-11">
                                                  <div className="account-data">
                                                    <p className="card-text d-flex justify-content-between align-items-center">
                                                      <span>
                                                        <b>
                                                          {acc.bank_account_type_category ===
                                                          "BANK"
                                                            ? "Account Name"
                                                            : "Name"}
                                                        </b>{" "}
                                                        :{" "}
                                                        {acc.display_holder_name ||
                                                          acc.holder_name ||
                                                          "—"}
                                                      </span>
                                                      <button
                                                        type="button"
                                                        className="btn btn-sm"
                                                        style={{
                                                          position: "relative",
                                                        }}
                                                        onClick={() =>
                                                          handleCopy(
                                                            acc.display_holder_name ||
                                                              acc.holder_name,
                                                            `name-${getEntityId(acc)}`,
                                                          )
                                                        }
                                                      >
                                                        <Copy
                                                          width="15"
                                                          height="15"
                                                        />
                                                        {copiedField ===
                                                          `name-${getEntityId(acc)}` && (
                                                          <div className="copy-tooltip">
                                                            Copied!
                                                          </div>
                                                        )}
                                                      </button>
                                                    </p>
                                                    <p className="card-text d-flex justify-content-between align-items-center">
                                                      <span>
                                                        <b>
                                                          {acc.account_number
                                                            ? "A/C No"
                                                            : acc.upi_id
                                                              ? "UPI ID"
                                                              : "Crypto Wallet"}
                                                        </b>{" "}
                                                        :{" "}
                                                        {acc.account_number ||
                                                          acc.upi_id ||
                                                          acc.crypto_wallet ||
                                                          "—"}
                                                      </span>
                                                      <button
                                                        type="button"
                                                        className="btn btn-sm"
                                                        style={{
                                                          position: "relative",
                                                        }}
                                                        onClick={() =>
                                                          handleCopy(
                                                            acc.account_number ||
                                                              acc.upi_id ||
                                                              acc.crypto_wallet,
                                                            `number-${getEntityId(acc)}`,
                                                          )
                                                        }
                                                      >
                                                        <Copy
                                                          width="15"
                                                          height="15"
                                                        />
                                                        {copiedField ===
                                                          `number-${getEntityId(acc)}` && (
                                                          <div className="copy-tooltip">
                                                            Copied!
                                                          </div>
                                                        )}
                                                      </button>
                                                    </p>
                                                    {acc.bank_name && (
                                                      <p className="card-text d-flex justify-content-between align-items-center">
                                                        <span>
                                                          <b>Bank Name</b> :{" "}
                                                          {acc.bank_name}
                                                        </span>
                                                        <button
                                                          type="button"
                                                          className="btn btn-sm"
                                                          style={{
                                                            position:
                                                              "relative",
                                                          }}
                                                          onClick={() =>
                                                            handleCopy(
                                                              acc.bank_name,
                                                              `bank-${getEntityId(acc)}`,
                                                            )
                                                          }
                                                        >
                                                          <Copy
                                                            width="15"
                                                            height="15"
                                                          />
                                                          {copiedField ===
                                                            `bank-${getEntityId(acc)}` && (
                                                            <div className="copy-tooltip">
                                                              Copied!
                                                            </div>
                                                          )}
                                                        </button>
                                                      </p>
                                                    )}
                                                    {acc.ifsc_code && (
                                                      <p className="card-text d-flex justify-content-between align-items-center">
                                                        <span>
                                                          <b>IFSC Code</b> :{" "}
                                                          {acc.ifsc_code}
                                                        </span>
                                                        <button
                                                          type="button"
                                                          className="btn btn-sm"
                                                          style={{
                                                            position:
                                                              "relative",
                                                          }}
                                                          onClick={() =>
                                                            handleCopy(
                                                              acc.ifsc_code,
                                                              `ifsc-${getEntityId(acc)}`,
                                                            )
                                                          }
                                                        >
                                                          <Copy
                                                            width="15"
                                                            height="15"
                                                          />
                                                          {copiedField ===
                                                            `ifsc-${getEntityId(acc)}` && (
                                                            <div className="copy-tooltip">
                                                              Copied!
                                                            </div>
                                                          )}
                                                        </button>
                                                      </p>
                                                    )}
                                                    <p className="card-text">
                                                      <b>Min Amount</b> :{" "}
                                                      {acc.min_amount || "0"}
                                                    </p>
                                                    <p className="card-text">
                                                      <b>Max Amount</b> :{" "}
                                                      {acc.max_amount || "—"}
                                                    </p>
                                                    {acc.image && (
                                                      <img
                                                        className="qr-code"
                                                        src={acc.image}
                                                        alt="QR Code"
                                                      />
                                                    )}
                                                  </div>
                                                </div>
                                                <div className="col-md-11">
                                                  <a
                                                    href="https://upitobank.xyz"
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                  >
                                                    <div className="card">
                                                      <div className="card-body upi-bank">
                                                        <p>
                                                          <b>
                                                            How To Transfer Upi
                                                            To Bank
                                                            <br />
                                                            CLICK HERE
                                                            WWW.UPITOBANK.XYZ
                                                          </b>
                                                        </p>
                                                      </div>
                                                    </div>
                                                  </a>
                                                </div>
                                                <div className="col-md-11">
                                                  <button
                                                    type="button"
                                                    className="p-0 border-0 bg-transparent w-100 text-start"
                                                    onClick={() =>
                                                      whatsappUrl &&
                                                      window.open(whatsappUrl, "_blank")
                                                    }
                                                  >
                                                    <div className="card">
                                                      <div className="card-body payment-issues">
                                                        <p>
                                                          <b>
                                                            FOR PAYMENT RELATED
                                                            ISSUES CLICK HERE
                                                          </b>
                                                          &nbsp;&nbsp;
                                                          <WhatsApp
                                                            height={12}
                                                            width={12}
                                                          />
                                                        </p>
                                                      </div>
                                                    </div>
                                                  </button>
                                                </div>
                                              </div>
                                            </div>
                                            <div className="col-md-6">
                                              <form
                                                onSubmit={(e) => {
                                                  e.preventDefault();
                                                  handleDepositSubmit(
                                                    getEntityId(acc),
                                                  );
                                                }}
                                              >
                                                <div className="modal-body">
                                                  <div className="form-group">
                                                    <label>
                                                      Unique Transaction
                                                      Reference{" "}
                                                      <small
                                                        style={{ color: "red" }}
                                                      >
                                                        *
                                                      </small>
                                                    </label>
                                                    <input
                                                      type="text"
                                                      className="form-control"
                                                      placeholder="Enter Transaction ID"
                                                      value={utrNumber}
                                                      disabled={isProcessingOCR}
                                                      onChange={(e) => {
                                                        setUtrNumber(
                                                          e.target.value,
                                                        );
                                                        setRefNoSource(
                                                          "manual",
                                                        );
                                                        setFormErrors(
                                                          (prev) => ({
                                                            ...prev,
                                                            utrNumber: null,
                                                          }),
                                                        );
                                                      }}
                                                    />
                                                    {formErrors.utrNumber && (
                                                      <label
                                                        id="transactionId-error"
                                                        className="error"
                                                        htmlFor="transactionId"
                                                      >
                                                        {formErrors.utrNumber}
                                                      </label>
                                                    )}
                                                  </div>
                                                  <div className="form-group">
                                                    <label>
                                                      Upload Your Payment Proof{" "}
                                                      <small
                                                        style={{ color: "red" }}
                                                      >
                                                        [Required]
                                                      </small>
                                                    </label>
                                                    <input
                                                      type="file"
                                                      className="form-control"
                                                      accept="image/*"
                                                      onChange={(e) => {
                                                        handleCapture(e);
                                                        setFormErrors(
                                                          (prev) => ({
                                                            ...prev,
                                                            proofFile: null,
                                                          }),
                                                        );
                                                      }}
                                                      disabled={isProcessingOCR}
                                                    />
                                                    {formErrors.proofFile && (
                                                      <label
                                                        id="proofFile-error"
                                                        className="error"
                                                        htmlFor="proofFile"
                                                      >
                                                        {formErrors.proofFile}
                                                      </label>
                                                    )}
                                                    {isProcessingOCR && (
                                                      <small className="form-text text-info mt-1 d-block mb-0">
                                                        <i className="bi bi-hourglass-split me-1"></i>{" "}
                                                        Analyzing image...
                                                      </small>
                                                    )}
                                                  </div>
                                                  <div className="form-group">
                                                    <label>
                                                      Amount{" "}
                                                      <small
                                                        style={{ color: "red" }}
                                                      >
                                                        *
                                                      </small>
                                                    </label>
                                                    <input
                                                      type="number"
                                                      className="form-control"
                                                      value={amount}
                                                      readOnly
                                                    />
                                                    {isPaytmSelected && (
                                                      <div className="btn-group">
                                                        {amountShortcuts.map(
                                                          (shortcutAmount) => (
                                                            <button
                                                              key={
                                                                shortcutAmount
                                                              }
                                                              type="button"
                                                              className="amount-shortcut"
                                                              data-amount={
                                                                shortcutAmount
                                                              }
                                                              onClick={() =>
                                                                handleAmountShortcutClick(
                                                                  shortcutAmount,
                                                                )
                                                              }
                                                            >
                                                              +
                                                              {new Intl.NumberFormat(
                                                                "en-IN",
                                                              ).format(
                                                                shortcutAmount,
                                                              )}
                                                            </button>
                                                          ),
                                                        )}
                                                      </div>
                                                    )}
                                                  </div>
                                                  <div className="custom-control custom-checkbox">
                                                    <input
                                                      type="checkbox"
                                                      className="custom-control-input"
                                                      name="terms_condition"
                                                      id="termsCheck97161"
                                                      checked={termsAccepted}
                                                      onChange={(e) => {
                                                        setTermsAccepted(
                                                          e.target.checked,
                                                        );
                                                        setFormErrors(
                                                          (prev) => ({
                                                            ...prev,
                                                            termsAccepted: null,
                                                          }),
                                                        );
                                                      }}
                                                    />
                                                    <label
                                                      className="custom-control-label"
                                                      htmlFor="termsCheck97161"
                                                    >
                                                      I have read and agree with{" "}
                                                      <span
                                                        onClick={(e) => {
                                                          e.preventDefault();
                                                          setShowTermsModal(
                                                            true,
                                                          );
                                                        }}
                                                        className="terms-condition mx-1"
                                                        style={{
                                                          cursor: "pointer",
                                                          color:
                                                            "var(--theme-primary-color)",
                                                          fontWeight: "bold",
                                                        }}
                                                      >
                                                        the terms of payment and
                                                        withdrawal policy.
                                                      </span>
                                                    </label>
                                                    {formErrors.termsAccepted && (
                                                      <label
                                                        id="terms_condition-error"
                                                        className="error"
                                                        htmlFor="termsCheck97161"
                                                      >
                                                        {
                                                          formErrors.termsAccepted
                                                        }
                                                      </label>
                                                    )}
                                                  </div>
                                                </div>
                                                <div className="modal-footer">
                                                  <button
                                                    type="submit"
                                                    className="btn btn-info depositBtn"
                                                    disabled={
                                                      isSubmitting ||
                                                      isProcessingOCR
                                                    }
                                                  >
                                                    {isSubmitting ||
                                                    isProcessingOCR
                                                      ? "SUBMITTING..."
                                                      : "Submit"}
                                                  </button>
                                                </div>
                                              </form>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {step === 0 && (
                  <div className="col-md-12">
                    <div className="card">
                      <div
                        className="card-body text-left card-padding"
                        style={{ fontSize: "10px", color: "red" }}
                      >
                        <p>
                          1. Deposit money only in the below available accounts
                          to get the fastest credits and avoid possible delays.
                        </p>
                        <p>
                          2. Deposits made 45 minutes after the account removal
                          from the site are valid &amp; will be added to their
                          wallets.
                        </p>
                        <p>
                          3. Site is not responsible for money deposited to Old,
                          Inactive or Closed accounts.
                        </p>
                        <p>
                          4. After deposit, add your UTR and amount to receive
                          balance.{" "}
                        </p>
                        <p>
                          5. NEFT receiving time varies from 40 minutes to 2
                          hours.{" "}
                        </p>
                        <p>
                          6. In case of account modification: payment valid for
                          1 hour after changing account details in deposit
                          page.{" "}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-4">
              <div className="account-table">
                <table className="table account-table-new">
                  <thead>
                    <tr>
                      <th>Transaction No</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingWallet ? (
                      <tr>
                        <td colSpan="5">Loading records...</td>
                      </tr>
                    ) : depositTransactions.length > 0 ? (
                      depositTransactions.map((tx, idx) => (
                        <tr key={tx.transactionId || idx}>
                          <td>{tx.transactionId}</td>
                          <td>₹{tx.amount}</td>
                          <td
                            className={`${
                              tx.txStatus === "ACCEPTED"
                                ? "badge badge-success"
                                : tx.txStatus === "REJECTED"
                                  ? "badge badge-danger"
                                  : "badge badge-warning"
                            }`}
                          >
                            {tx.txStatus}
                          </td>
                          <td>{new Date(tx.startTime).toLocaleString()}</td>
                          <td>{tx.notes}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5">No data found!</td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {/* <div className="row video-player">
                  <div className="col-md-12 ">
                    <label>Please note the UTR Number👇</label>
                    <img
                      src="https://a2t.land/images/utr_demo_img.png"
                      height="300"
                      width="320"
                      alt="UTR Demo"
                    />
                  </div>
                  <div className="col-md-12 mb-2">
                    <label>Phonepay To Bank Transfer👇</label>
                    <Link
                      to="https://appopener.ai/yt/7ku56ivmr"
                      target="_blank"
                    >
                      https://appopener.ai/yt/7ku56ivmr
                    </Link>
                  </div>
                  <div className="col-md-12 mb-2">
                    <label>Gpay To Bank Transfer 👇</label>
                    <Link
                      to="https://appopener.ai/yt/b6ngpraah"
                      target="_blank"
                    >
                      https://appopener.ai/yt/b6ngpraah
                    </Link>
                  </div>
                  <div className="col-md-12 mb-2">
                    <label>Paytm To Bank Transfer 👇</label>
                    <Link
                      to="https://appopener.ai/yt/e7i9e3623"
                      target="_blank"
                    >
                      https://appopener.ai/yt/e7i9e3623
                    </Link>
                  </div>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Terms and Conditions Modal */}
      <Dialog
        show={showTermsModal}
        handleClose={() => setShowTermsModal(false)}
        title="The terms of payment and withdrawal policy"
        closeButton={true}
        centered
      >
        <p>
          Limits The maximum deposit limit on your account will vary depending
          on how long you have been a customer with us and whether you have
          successfully passed our security checks. Please contact the{" "}
          <span className="terms-support">support@reddybook.club</span> for
          further information.
        </p>
        <p>
          Security Please note you may be asked to supply proof of identity and
          address before making a withdrawal from your account. Credit and Debit
          card payments undergo a 3D Secure process. To find out more, please
          click here.
        </p>
        <p>
          Withdrawals Withdrawals will be directed to the same source. If your
          query is not covered by the information contained here, please email{" "}
          <span className="terms-support">support@reddybook.club</span> where
          staff will be on hand to answer your query.
        </p>
      </Dialog>
    </div>
  );
};

export default Deposite;
